import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { vendorFurnitureService } from '@/services/VendorFurnitureService'
import { vendorFurnitureKeys } from '@/hooks/queryKeys'
import type { FurnitureItemDto, FurnitureItemListDto } from '@lib/dto/furniture-item.dto'
import type { FurnitureGroupDto, FurnitureGroupListDto } from '@lib/dto/furniture-group.dto'
import type { CreateVendorFurnitureItemInput, UpdateFurnitureItemInput } from '@lib/input/furniture-item.input'
import type { CreateVendorFurnitureGroupInput, UpdateFurnitureGroupInput } from '@lib/input/furniture-group.input'

/** The signed-in vendor's own furniture library. */
export function useVendorFurniture(): UseQueryResult<FurnitureItemListDto> {
  return useQuery({
    queryKey: vendorFurnitureKeys.items(),
    queryFn: () => vendorFurnitureService.listItems(),
    staleTime: 30_000,
  })
}

export function useCreateVendorFurniture(): UseMutationResult<FurnitureItemDto, Error, CreateVendorFurnitureItemInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateVendorFurnitureItemInput) => vendorFurnitureService.createItem(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorFurnitureKeys.items() }),
  })
}

export function useUpdateVendorFurniture(): UseMutationResult<FurnitureItemDto, Error, { id: string; input: UpdateFurnitureItemInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateFurnitureItemInput }) => vendorFurnitureService.updateItem(vars.id, vars.input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorFurnitureKeys.items() })
      // A renamed/reshaped item flows into groups that reference it.
      void queryClient.invalidateQueries({ queryKey: vendorFurnitureKeys.groups() })
    },
  })
}

/** The signed-in vendor's own furniture groups. */
export function useVendorFurnitureGroups(): UseQueryResult<FurnitureGroupListDto> {
  return useQuery({
    queryKey: vendorFurnitureKeys.groups(),
    queryFn: () => vendorFurnitureService.listGroups(),
    staleTime: 30_000,
  })
}

export function useCreateVendorFurnitureGroup(): UseMutationResult<FurnitureGroupDto, Error, CreateVendorFurnitureGroupInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateVendorFurnitureGroupInput) => vendorFurnitureService.createGroup(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorFurnitureKeys.groups() }),
  })
}

export function useUpdateVendorFurnitureGroup(): UseMutationResult<FurnitureGroupDto, Error, { id: string; input: UpdateFurnitureGroupInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateFurnitureGroupInput }) => vendorFurnitureService.updateGroup(vars.id, vars.input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorFurnitureKeys.groups() }),
  })
}
